import { useEffect, useRef, useState } from "react";
import { cellGeometry, photoSrc } from "@/lib/designer";

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

const Cell = ({ rect, size, cell, photo, index, selected, readOnly, quality, missing, swapping, onSelect, onLive, onStart, onEnd, onAdd }) => {
  const cw = rect[2] * size.w, ch = rect[3] * size.h;
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const geo = photo ? cellGeometry(photo, cell, cw, ch) : null;

  const begin = () => { if (!gesture.current) { gesture.current = { cell: { ...cell } }; onStart(); } };
  const onDown = (e) => {
    if (readOnly) return;
    onSelect(index);
    if (!photo || swapping) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    begin();
    gesture.current.base = { cell: { ...cell }, pts: [...pointers.current.values()].map((p) => ({ ...p })) };
  };
  const onMove = (e) => {
    if (!gesture.current || !pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const { base } = gesture.current;
    const pts = [...pointers.current.values()];
    if (pts.length >= 2 && base.pts.length >= 2) {
      const d0 = Math.hypot(base.pts[0].x - base.pts[1].x, base.pts[0].y - base.pts[1].y);
      const d1 = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      onLive(index, { zoom: clamp(base.cell.zoom * (d1 / (d0 || 1)), 1, 5) });
    } else if (pts.length === 1 && base.pts.length === 1) {
      const g = cellGeometry(photo, base.cell, cw, ch);
      const dx = pts[0].x - base.pts[0].x, dy = pts[0].y - base.pts[0].y;
      onLive(index, {
        fx: g.overX > 0.5 ? clamp(base.cell.fx - dx / g.overX, 0, 1) : 0.5,
        fy: g.overY > 0.5 ? clamp(base.cell.fy - dy / g.overY, 0, 1) : 0.5,
      });
    }
  };
  const onUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0 && gesture.current) { onEnd(gesture.current.cell); gesture.current = null; }
    else if (gesture.current) gesture.current.base = { cell: { ...cell }, pts: [...pointers.current.values()] };
  };
  const onWheel = (e) => {
    if (readOnly || !photo) return;
    e.preventDefault();
    begin();
    onLive(index, { zoom: clamp(cell.zoom * Math.exp(-e.deltaY * 0.0015), 1, 5) });
    clearTimeout(gesture.current.t);
    gesture.current.t = setTimeout(() => { onEnd(gesture.current.cell); gesture.current = null; }, 350);
  };

  const el = useRef(null);
  const wheelRef = useRef(null);
  wheelRef.current = onWheel;
  useEffect(() => {
    const node = el.current;
    const fn = (e) => wheelRef.current(e);
    node.addEventListener("wheel", fn, { passive: false });
    return () => node.removeEventListener("wheel", fn);
  }, []);

  return (
    <div ref={el} data-testid={`design-cell-${index}`} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
      className={`absolute overflow-hidden bg-[#EAE7E2] ${readOnly ? "" : photo ? "cursor-grab active:cursor-grabbing touch-none" : "cursor-pointer"}`}
      style={{ left: rect[0] * size.w, top: rect[1] * size.h, width: cw, height: ch }}>
      {photo ? (
        <div className="pointer-events-none absolute" style={geo.wrap}>
          <img src={photoSrc(photo.id)} alt="" draggable={false} className="absolute max-w-none select-none" style={geo.img} />
        </div>
      ) : !readOnly && (
        <button type="button" data-testid={`design-cell-${index}-add`} onClick={() => !swapping && onAdd(index)}
          className={`absolute inset-0 grid place-items-center text-[0.7rem] font-medium uppercase tracking-[0.12em] hover:text-[#080808] ${missing ? "border-2 border-dashed border-[#8B1E1E] bg-[#8B1E1E]/5 text-[#8B1E1E]" : "text-[#080808]/55"}`}>
          {missing ? `Falta foto ${index + 1}` : `+ Foto ${index + 1}`}
        </button>
      )}
      {!readOnly && swapping && <div className="pointer-events-none absolute inset-0 ring-2 ring-inset ring-[#080808]/40 ring-offset-0" />}
      {!readOnly && selected && <div className="pointer-events-none absolute inset-0 ring-2 ring-inset ring-[#B18A4B]" />}
      {!readOnly && (quality === "ok" || quality === "low") && (
        <span data-testid={`design-cell-${index}-quality`} className={`pointer-events-none absolute left-1.5 top-1.5 rounded-full px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide ${quality === "low" ? "bg-[#8B1E1E] text-white" : "bg-[#D9B978] text-[#080808]"}`}>
          {quality === "low" ? "Baja resolución" : "Aceptable"}
        </span>
      )}
    </div>
  );
};

export const DesignCanvas = ({ wcm, hcm, rects, cells, photos, selected, readOnly, qualities, maxH = 560, onSelect, onLive, onStart, onEnd, onAdd, missing, swapping, testId = "design-canvas" }) => {
  const box = useRef(null);
  const [avail, setAvail] = useState(400);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setAvail(e.contentRect.width));
    ro.observe(box.current);
    return () => ro.disconnect();
  }, []);
  const scale = Math.min(avail / wcm, maxH / hcm);
  const size = { w: Math.floor(wcm * scale), h: Math.floor(hcm * scale) };
  const radius = Math.max(4, size.w * 0.018);
  const selRect = rects[selected], selCell = cells[selected], selPhoto = selCell && photos[selCell.photo_id];
  const ghost = !readOnly && selRect && selPhoto ? cellGeometry(selPhoto, selCell, selRect[2] * size.w, selRect[3] * size.h) : null;
  return (
    <div ref={box} className="w-full">
      <div data-testid={testId} className="relative mx-auto select-none" style={{ width: size.w, height: size.h }}>
        {ghost && (
          <div data-testid="crop-ghost" className="pointer-events-none absolute opacity-30" style={{ ...ghost.wrap, left: selRect[0] * size.w + ghost.wrap.left, top: selRect[1] * size.h + ghost.wrap.top }}>
            <img src={photoSrc(selPhoto.id)} alt="" className="absolute max-w-none" style={ghost.img} />
          </div>
        )}
        <div className="absolute inset-0 overflow-hidden" style={{ borderRadius: readOnly ? radius : 0 }}>
          {rects.map((r, i) => (
            <Cell key={i} rect={r} size={size} index={i} cell={cells[i]} photo={photos[cells[i]?.photo_id]} selected={selected === i}
              readOnly={readOnly} quality={qualities?.[i]} missing={missing && !photos[cells[i]?.photo_id]} swapping={swapping}
              onSelect={onSelect} onLive={onLive} onStart={onStart} onEnd={onEnd} onAdd={onAdd} />
          ))}
        </div>
        {readOnly
          ? <div className="pointer-events-none absolute inset-0 shadow-[10px_14px_24px_-8px_rgba(8,8,8,0.35)]" style={{ borderRadius: radius }} />
          : <div className="pointer-events-none absolute inset-0 border border-dashed border-[#080808]/60" style={{ borderRadius: radius }} />}
      </div>
    </div>
  );
};
